// src/components/DataVisualizer.jsx
import React,{useState,useEffect,useRef} from "react";
import {fetchEPU} from "../services/epuApi";
import "chartjs-adapter-date-fns";
import html2canvas from "html2canvas";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  TimeScale,
  CategoryScale,
  LinearScale,
  Tooltip,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "./DataVisualizer.css";
import annotationPlugin from "chartjs-plugin-annotation";

const API_URL = 'http://localhost:5000';


const hoverLinePlugin = {
  id: "hoverLine",
  
  afterDraw(chart) {
    const active = chart.tooltip?._active;

    if (!active || active.length === 0) return;

    const ctx = chart.ctx;
    const point = active[0].element;
    const area = chart.chartArea;

    ctx.save();

    ctx.beginPath();
    ctx.setLineDash([]);
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#1a1a2e";

    ctx.moveTo(point.x, area.top);
    ctx.lineTo(point.x, area.bottom);
    // ctx.moveTo(area.left, point.y);
    // ctx.lineTo(area.right, point.y);

    ctx.stroke();
    ctx.restore();
  }
};

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Filler,
  annotationPlugin,
  hoverLinePlugin
);

Tooltip.positioners.above = function(elements) {
  if (!elements.length) return false;

  const point = elements[0].element;

  return {
    x: point.x,
    y: point.y - 40
  };
};

function DataVisualizer({
  source="poland",
  frequency="monthly",
  embed=false,
  embedStartDate,
  embedEndDate,
  embedRange
}){

  const [currentFrequency,setCurrentFrequency] = useState(frequency);
  const [shareOpen,setShareOpen] = useState(false);
  const [apiData,setApiData] = useState(null);
  const [loading,setLoading] = useState(true);
  const [frequencyOpen,setFrequencyOpen] = useState(false);
  const [startDate,setStartDate] = useState("");
  const [endDate,setEndDate] = useState("");
  const [dateRange,setDateRange] = useState("10Y");

  const chartRef = useRef(null);
  const shareRef = useRef(null);
  const downloadRef = useRef(null);
  const [fullscreen,setFullscreen] = useState(false);
  const [downloadOpen,setDownloadOpen] = useState(false);
  const [embedOpen,setEmbedOpen] = useState(false);
  const [customLinkOpen, setCustomLinkOpen] = useState(false);

  const [selectedEmbedRange,setSelectedEmbedRange] = useState("latest");
  const [copied,setCopied] = useState(false);
  const [hoverEvent, setHoverEvent] = React.useState(null);
  


  const toggleFullscreen = () => {
  if(!document.fullscreenElement){
    chartRef.current.requestFullscreen();
    setFullscreen(true);
  }else{
    document.exitFullscreen();
    setFullscreen(false);
  }
  };

  const chartAreaBackground = {
    id:"chartAreaBackground",
    beforeDraw(chart){
      const {ctx, chartArea}=chart;
      if(!chartArea) return;
      ctx.save();
      ctx.fillStyle="#ffffff";
      ctx.fillRect(
        chartArea.left,
        chartArea.top,
        chartArea.right-chartArea.left,
        chartArea.bottom-chartArea.top
      );
      ctx.restore();
    }
  };

  useEffect(()=>{

    const handleClickOutside = (event)=>{

      if(
        downloadRef.current &&
        !downloadRef.current.contains(event.target)
      ){
        setDownloadOpen(false);
      }

    };


    document.addEventListener(
      "mousedown",
      handleClickOutside
    );


    return ()=>{

      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );

    };

  },[]);


  useEffect(()=>{

    const handleClickOutside = (event)=>{

      if(
        shareRef.current &&
        !shareRef.current.contains(event.target)
      ){
        setShareOpen(false);
      }

    };


    document.addEventListener(
      "mousedown",
      handleClickOutside
    );


    return ()=>{

      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );

    };

  },[]);
  // // ✅ Add fetch here
  useEffect(()=>{
    setLoading(true);
    fetchEPU(source,currentFrequency)
    .then(result=>{
      setApiData(result);
      const data = result.data;
      setStartDate(data[0].date);
      setEndDate(data[data.length-1].date);
      setLoading(false);
    })
    .catch(error=>{
      console.error(error);
      setLoading(false);
    });
  },[source,currentFrequency]);


  useEffect(()=>{
    if(!apiData || !apiData.data){
      return;
    }
    // Embed mode: use iframe dates
    if(embedStartDate || embedEndDate){
      if(embedStartDate){
        setStartDate(embedStartDate);
      }
      if(embedEndDate){
        setEndDate(embedEndDate);
      }
      return;
    }
    const data = apiData.data;
    const latest = data[data.length-1].date;
    const first = data[0].date;

    if(dateRange==="Max"){
      setStartDate(first);
      setEndDate(latest);
      return;
    }

    let start = new Date(latest);

    if(dateRange==="1Y"){
      start.setFullYear(
        start.getFullYear()-1
      );
    }

    if(dateRange==="5Y"){
      start.setFullYear(
        start.getFullYear()-5
      );
    }

    if(dateRange==="10Y"){
      start.setFullYear(
        start.getFullYear()-10
      );
    }

    setStartDate(
      start.toISOString().split("T")[0]
    );

    setEndDate(latest);

  },[apiData, dateRange, embedStartDate, embedEndDate]);


  const filteredData = React.useMemo(()=>{
    if(!apiData || !apiData.data){
    return [];
    }
    return apiData.data.filter(item=>{
      const current =
      new Date(item.date);
      return (
        (!startDate ||
        current >= new Date(startDate))
        &&
        (!endDate ||
        current <= new Date(endDate))
      );
      });
    },[apiData, startDate, endDate]);


  const labels = filteredData.map(
    item=>item.date
  );

  const values = filteredData.map(
    item=>item.value
  );

  const visibleEvents = React.useMemo(() => {
    if (!apiData?.events || !labels?.length) {
      return [];
    }
    const firstDate = new Date(labels[0]);
    const lastDate = new Date(labels[labels.length - 1]);
    return apiData.events.filter(event => {
      const eventDate = new Date(event.date);
      return (
        eventDate >= firstDate &&
        eventDate <= lastDate &&
        event.status.includes("include")
      );
    });
  }, [
    apiData?.events,
    startDate, 
    endDate
  ]);

  const categoryStyles = {
    policy_shock: {
      line: "rgba(220, 53, 69, 0.65)",
      border: "#dc3545",
      text: "#842029",
      background: "rgba(255, 245, 246, 0.96)"
    },

    non_policy_control: {
      line: "rgba(108, 117, 125, 0.60)",
      border: "#6c757d",
      text: "#495057",
      background: "rgba(248, 249, 250, 0.96)"
    },

    context_no_reaction: {
      line: "rgba(181, 148, 16, 0.60)",
      border: "#b59410",
      text: "#705d00",
      background: "rgba(255, 252, 235, 0.96)"
    },

    us_election: {
      line: "rgba(52, 152, 219, 0.65)",
      border: "#3498db",
      text: "#21618c",
      background: "rgba(240, 248, 255, 0.96)"
    },

    us_inauguration: {
      line: "rgba(52, 152, 219, 0.65)",
      border: "#3498db",
      text: "#21618c",
      background: "rgba(240, 248, 255, 0.96)"
    },

    pl_government: {
      line: "rgba(126, 87, 194, 0.60)",
      border: "#7e57c2",
      text: "#5e35b1",
      background: "rgba(248, 245, 255, 0.96)"
    }
  };


  // const eventAnnotations = React.useMemo(() => {
  //   const annotations = {};
  //   visibleEvents.forEach((event, index) => {

  //     annotations[`event_${index}`] = {
  //       type: "line",

  //       xMin: event.date,
  //       xMax: event.date,

  //       borderColor: "#e63946",
  //       borderWidth: 2,
  //       borderDash: [6, 4],

  //       label: {
  //         display: true,
  //         content: event.label,
  //         position: "start",

  //         backgroundColor: "#e63946",
  //         color: "#ffffff",

  //         font: {
  //           size: 11
  //         },

  //         padding: 5
  //       }
  //     };

  //   });
  //   return annotations;
  // }, [visibleEvents]);


  // const eventAnnotations = React.useMemo(() => {

  //   const annotations = {};

  //   visibleEvents.forEach((event, index) => {

  //     const style =
  //       categoryStyles[event.category] ||
  //       categoryStyles.context_no_reaction;

  //     annotations[`event_${index}`] = {

  //       type: "line",

  //       xMin: event.date,
  //       xMax: event.date,

  //       borderColor: style.line,
  //       borderWidth: 1.5,
  //       borderDash: [5, 5],

  //       label: {

  //         display: true,

  //         content: event.label,

  //         position: "start",

  //         backgroundColor: style.background,

  //         borderColor: style.border,
  //         borderWidth: 1,

  //         borderRadius: 7,

  //         color: style.text,

  //         font: {
  //           size: 10,
  //           weight: "600"
  //         },

  //         padding: {
  //           top: 5,
  //           bottom: 5,
  //           left: 8,
  //           right: 8
  //         },

  //         yAdjust: 0
  //       }
  //     };

  //   });

  //   return annotations;

  // }, [visibleEvents]);

  const eventAnnotations = React.useMemo(() => {
    const annotations = {};

    visibleEvents.forEach((event, index) => {

      const style =
        categoryStyles[event.category] ||
        categoryStyles.context_no_reaction;

      annotations[`event_${index}`] = {

        type: "line",

        xMin: event.date,
        xMax: event.date,

        borderColor: style.color,
        borderWidth: 1.5,
        borderDash: [5, 5],

        label: {

          // Hidden initially
          display: false,

          content: event.label,

          position: "start",

          backgroundColor: style.color,

          borderColor: style.color,
          borderWidth: 1,

          borderRadius: 6,

          color: style.text,

          font: {
            size: 10,
            weight: "600"
          },

          padding: {
            top: 5,
            bottom: 5,
            left: 8,
            right: 8
          }
        }
      };

    });

    return annotations;

  }, [visibleEvents]);
  
  const chartData = React.useMemo(()=>({
    labels,
    datasets:[
      {
        label:apiData?.metadata?.title || "",
        data:values,
        borderColor:"#1780e8",
        backgroundColor:"rgba(23,128,232,0.08)",
        borderWidth:2.5,
        fill:false,
        tension:0,
        pointRadius:0,
        pointHoverRadius:0,
        pointBackgroundColor:"#1780e8",
        pointBorderColor:"#ffffff"
      }
    ]
  }),[labels,values]);
  

  // const eventLinePlugin = React.useMemo(() => ({
  //   id: "eventLinePlugin",
  //   afterDraw(chart) {
  //     const {ctx, chartArea, scales} = chart;
  //     visibleEvents.forEach(event => {
  //       const x = scales.x.getPixelForValue(new Date(event.date));
  //       if (x < chartArea.left || x > chartArea.right) {
  //         return;
  //       }
  //       ctx.save();
  //       ctx.beginPath();
  //       ctx.moveTo(x, chartArea.top);
  //       ctx.lineTo(x, chartArea.bottom);
  //       ctx.strokeStyle = "rgb(254, 3, 3)";
  //       ctx.lineWidth = 1;
  //       ctx.setLineDash([5,5]);
  //       ctx.stroke();
  //       ctx.restore();
  //     });
  //   },
  //   afterEvent(chart, args) {
  //     const {event} = args;
  //     const mouseX = event.x;
  //     const mouseY = event.y;
  //     let hoveredEvent = null;
  //     visibleEvents.forEach(item => {
  //       const eventX = chart.scales.x.getPixelForValue(
  //         new Date(item.date)
  //       );
  //       if (Math.abs(eventX - mouseX) < 8) {
  //         hoveredEvent = item;
  //       }
  //     });
  //     // remove old tooltip
  //     chart.canvas.title = "";
  //     if (hoveredEvent) {

  //       chart.canvas.title =
  //         `${hoveredEvent.label} (${hoveredEvent.date})`;

  //       chart.draw();
  //     }
  //   }
  // }), [visibleEvents]);

  // const eventLinePlugin = React.useMemo(() => ({
  //   id: "eventLinePlugin",
  //   afterDraw(chart) {
  //     const { ctx, chartArea, scales } = chart;
  //     if (!chartArea || !scales.x) return;
  //     visibleEvents.forEach(event => {
  //       const x = scales.x.getPixelForValue(
  //         new Date(event.date)
  //       );

  //       if (
  //         x < chartArea.left ||
  //         x > chartArea.right
  //       ) {
  //         return;
  //       }

  //       const style =
  //         categoryStyles[event.category] ||
  //         categoryStyles.context_no_reaction;

  //       ctx.save();

  //       ctx.beginPath();

  //       ctx.moveTo(x, chartArea.top);
  //       ctx.lineTo(x, chartArea.bottom);

  //       ctx.strokeStyle = style.line;
  //       ctx.lineWidth = 1;
  //       ctx.setLineDash([5, 5]);

  //       ctx.stroke();

  //       ctx.restore();
  //     });
  //   },


  //   afterEvent(chart, args) {

  //     const { event } = args;

  //     const mouseX = event.x;
  //     const mouseY = event.y;

  //     let hoveredEvent = null;

  //     visibleEvents.forEach(item => {

  //       const eventX =
  //         chart.scales.x.getPixelForValue(
  //           new Date(item.date)
  //         );

  //       if (Math.abs(eventX - mouseX) < 8) {
  //         hoveredEvent = item;
  //       }

  //     });


  //     // Remove previous tooltip
  //     chart.canvas.title = "";


  //     if (hoveredEvent) {

  //       chart.canvas.title =
  //         `${hoveredEvent.label} (${hoveredEvent.date})`;

  //     }

  //   }

  // }), [visibleEvents]);

  const eventLinePlugin = React.useMemo(() => ({
    id: "eventLinePlugin",
    afterDraw(chart) {
      const { ctx, chartArea, scales } = chart;
      if (!chartArea || !scales.x) return;
      visibleEvents.forEach(event => {
        const x = scales.x.getPixelForValue(
          new Date(event.date)
        );
        if (
          x < chartArea.left ||
          x > chartArea.right
        ) {
          return;
        }
        const style =
          categoryStyles[event.category] ||
          categoryStyles.context_no_reaction;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.strokeStyle = style.line;
        ctx.lineWidth = 1;
        ctx.setLineDash([5, 5]);
        ctx.stroke();
        ctx.restore();
      });
    },


    afterEvent(chart, args) {

      const { event } = args;

      let hoveredEvent = null;

      visibleEvents.forEach(item => {

        const eventX =
          chart.scales.x.getPixelForValue(
            new Date(item.date)
          );

        if (Math.abs(eventX - event.x) < 8) {
          hoveredEvent = item;
        }

      });
      // Remove existing tooltip
      chart.canvas.title = "";
      if (hoveredEvent) {

        const style =
          categoryStyles[hoveredEvent.category] ||
          categoryStyles.context_no_reaction;

        chart.canvas.title =
          `${hoveredEvent.label} (${hoveredEvent.date})`;

      }
    }
  }), [visibleEvents]);




  // ✅ Add loading check here
  if(loading){

    return (
      <div>
        Loading data...
      </div>
    );

  }


  const options={
    responsive:true,
    maintainAspectRatio:false,

    interaction:{
      mode:"index",
      intersect:false
    },

    plugins:{
      legend:{
        display:false
      },

      tooltip:{
        position:"above",
        backgroundColor:"#ffffff",
        borderColor:"#0073e6",
        borderWidth:1,
        padding:10,
        caretSize:0,
        displayColors:false,
        titleColor:"#0b1d32",
        bodyColor:"#0b1d32",

        callbacks:{
          title(){
            return "";
          },

          label(context){
            const date = new Date(context.parsed.x);

            if(currentFrequency === "monthly"){
              const formattedMonth = date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short"
              });

              return `${formattedMonth}: ${context.parsed.y}`;
            }

            const formattedDate = date.toISOString().split("T")[0];

            return `${formattedDate}: ${context.parsed.y}`;
          }
        }
      },

      annotation:{
        annotations:{
          referenceLine100:{
            type:"line",
            yMin:100,
            yMax:100,
            borderColor:"#000000",
            borderWidth:2,
            borderDash:[],
            label:{
              display:false,
              content:"100",
              position:"end"
            }
          }

        }
      }
    },
    scales:{
      x:{
        type:"time",

        time:{
          unit:"month",

          displayFormats:{
            month:"MMM yyyy"
          }
        },

        grid:{
          display:false
        },

        ticks:{
          autoSkip:true,
          maxTicksLimit:20,
          maxRotation:0,
          minRotation:0,

          color:"#444444",

          font:{
            size:11
          }
        }
      },
      y:{
        min:0,
        beginAtZero:true,
        ticks:{
          stepSize:200
        },
        title:{
          display:true,
          text:"Index",
          color:"#444444",
          font:{
            size:14,
            // weight:"bold"
          }
        },
        ticks:{
          stepSize:50,
          color:"#444444",
          font:{
            size:11
          }
        },

        grid:{
          color:"#cfd4dc",
          lineWidth:1
        }
      }
    }
  };
  


  const downloadCSV = async () => {
    const password = window.prompt(
      "Please enter password to download CSV:"
    );
    if(!password){
      return;
    }
    try{

      // Step 1: authenticate password
      const authResponse = await fetch(
        `${API_URL}/api/auth`,
        {
          method:"POST",
          headers:{
            "Content-Type":"application/json"
          },
          body:JSON.stringify({
            password
          })
        }
      );

      if(!authResponse.ok){

        alert("Invalid password");

        return;
      }

      const authData = await authResponse.json();
      const token = authData.token;
      // Step 2: download CSV
      const response = await fetch(
        `${API_URL}/api/download?type=${currentFrequency}`,
        {
          headers:{
            Authorization:`Bearer ${token}`
          }
        }
      );

      if(!response.ok){
        alert("Download failed");
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link=document.createElement("a");
      link.href=url;
      link.download=
        `${currentFrequency}_epu_data.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }catch(error){
      console.error(error);
      alert("Download error");
    }
  };

  const downloadPNG = async () => {
    if(!chartRef.current) return;
    const canvas = await html2canvas(
      chartRef.current,
      {
        backgroundColor:"#ebf3fb",
        scale:2,
        ignoreElements: (element) =>
        element.classList.contains("export-hide")
      }
    );
    const link=document.createElement("a");
    link.download="EPU_chart.png";
    link.href=canvas.toDataURL("image/png");
    link.click();
  };


  const getEmbedCode = () => {
    let rangeParam = "";
    if(selectedEmbedRange==="latest"){
      rangeParam =
        `start_date=2022-07-01`;
    }
    if(selectedEmbedRange==="5years"){
      rangeParam =
        `range=5years`;
    }
    if(selectedEmbedRange==="fixed"){
      rangeParam =
        `start_date=2022-07-01&end_date=2026-07-01`;
    }
    return `<iframe
    src="${window.location.origin}/embed/chart?${rangeParam}"
    scrolling="no"
    frameborder="0"
    style="overflow:hidden;width:100%;height:600px;"
    loading="lazy">
    </iframe>`;

  };

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(getEmbedCode());
    setCopied(true);

    setTimeout(()=>{
      setCopied(false);
    },1500);
  };


  const getCustomGraphLink = ()=>{
    let params="";
    if(selectedEmbedRange==="latest"){
      params=
      "start_date=2021-07-28";

    }
    if(selectedEmbedRange==="5years"){
      params=
      "range=5years";
    }
    if(selectedEmbedRange==="fixed"){
      params=
      "start_date=2021-07-28&end_date=2026-07-28";
    }
    return `${window.location.origin}/embed/chart?${params}`;
  };


  return (
    <main className="fred-main">
      <div className="fred-card">
        {/* Part1 Menu */}
        {!embed && (
          <div className="fred-grid">
            <div className="col observations-col">

              <div className="col-label">
                Observations
              </div>
              
              <div className="col-value">
                <strong>
                  {apiData.metadata.latestDate}: {apiData.metadata.latestValue}
                </strong>

                <div className="small-text">
                  Updated:
                  <span>
                    {apiData.metadata.latestDate}
                  </span>
                </div>
              </div>

            </div>

            <div className="col">
              <div className="col-label">
                Units
              </div>
              <div className="col-value">

                <strong>
                  {apiData.metadata.unit}
                </strong>

                <div className="small-text">
                  Not Seasonally Adjusted
                </div>

              </div>

            </div>

            <div className="col">

              <div className="col-label">
                Frequency
              </div>

              <div className="frequency-dropdown">

                <div
                  className="frequency-selected"
                  onClick={()=>{
                    setFrequencyOpen(!frequencyOpen);
                  }}
                >

                  <strong>
                    {currentFrequency === "daily" ? "Daily" : "Monthly"}
                  </strong>

                  <span className="frequency-arrow">
                    ▾
                  </span>

                </div>


                <div className="frequency-underline"></div>


                {
                  frequencyOpen &&

                  <div className="frequency-menu">

                    {/* <div
                      className="frequency-option"
                      onClick={()=>{
                        setCurrentFrequency("daily");
                        setFrequencyOpen(false);
                      }}
                    >
                      Daily
                    </div> */}
                    <div
                      className="frequency-option"
                      onClick={() => {
                        window.open('/contact.html', '_blank');
                      }}
                    >
                      Daily
                    </div>

                    <div
                      className="frequency-option"
                      onClick={()=>{
                        setCurrentFrequency("monthly");
                        setFrequencyOpen(false);
                      }}
                    >
                      Monthly
                    </div>

                  </div>

                }
              </div>

            </div>

            <div className="col">
              {/* <div className="col-label">
                Date Range
              </div> */}
              <div className="range-pills">
                {["1Y","5Y","10Y","Max"].map(range=>(
                <span
                key={range}
                className={
                dateRange===range
                ?"active"
                :""
                }
                onClick={()=>{
                  setDateRange(range);
                }}
                >
                {range}
                </span>
                ))
                }
              </div>

              <div className="date-inputs">

                <input
                type="date"
                value={startDate}
                onChange={(e)=>{

                setDateRange("");

                setStartDate(
                e.target.value
                );

                }}
                />


                <span>
                to
                </span>


                <input
                type="date"
                value={endDate}
                onChange={(e)=>{

                setDateRange("");

                setEndDate(
                e.target.value
                );

                }}
                />

              </div>

            </div>

            <div className="col">
              {/* <div className="col-label">
                Actions
              </div> */}
              <div className="action-buttons">
                {/* <button className="btn-edit">
                ✎ Edit Graph
                </button> */}
                <div className="download-wrapper" ref={downloadRef}>
                  <button className="btn-download"
                    onClick={()=>{ setDownloadOpen(!downloadOpen);
                    }}
                  >
                    ↓ Download
                  </button>

                  {downloadOpen &&
                    <div className="download-dropdown">

                      <button onClick={() => { window.open('/contact.html', '_blank'); }}>
                        🗎 CSV (Data)
                      </button>

                      <button onClick={downloadPNG}>
                      🖼 PNG (Image)
                      </button>

                    </div>
                  }

                </div>
                {/* Part3 ShareButton */}
                {!embed && (
                  <div className="share-wrapper" ref={shareRef}>

                    <button
                    className="share-btn"
                    onClick={()=>setShareOpen(!shareOpen)}
                    >
                    ↗ Share Graph
                    </button>


                    {
                    shareOpen &&
                      <div className="share-dropdown">
                        <button
                          className="btn-download"
                          onClick={() => setEmbedOpen(true)}
                        >
                          &lt;/&gt; Embed in Website
                        </button>
                        <button
                          className="btn-download"
                          onClick={()=>setCustomLinkOpen(true)}
                        >
                        🖼 Graph Image Link
                        </button>
                      </div>
                    }


                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Part2 ChartCard */}
        <div className="chart-card" ref={chartRef}>

          <div className="chart-header">

            <div className="fred-logo">

              <img src="/epupl-icon.png" alt="EPUPL icon" />

            </div>

            <div className="legend-item">

              <span className="legend-line"/>
                {apiData.metadata.title}
              <span>
                
              </span>

            </div>


          </div>

          <div className="chart-container">

            {hoverEvent && (
              <div className="event-tooltip">
                {hoverEvent.label}
              </div>
            )}

            <Line
              key={`${dateRange}-${startDate}-${endDate}`}
              data={chartData}
              options={options}
              plugins={[chartAreaBackground,
                        hoverLinePlugin,
                        eventLinePlugin]}
            />

          </div>

          <div className="chart-footer">

            <div>
              Sources: Alina Landowska, Robert Kłopotek, He Zhang via PL EPUI
            </div>

            <div className="footer-actions">
              <a
                href="https://epu-index.pl"
                target="_blank"
                rel="noopener noreferrer"
                className="source-link"
              >
                epu-index.pl
              </a>
              <button 
                className="fullscreen-btn export-hide"
                onClick={toggleFullscreen}
              >
                {fullscreen 
                  ? "⛶ Exit Fullscreen" 
                  : "⛶ Fullscreen"}
              </button>
            </div>

          </div>

        </div>
        
        {embedOpen && (
          <div className="embed-overlay">
          <div className="embed-modal">
          <h3>
            Embed in Website
          </h3>
          <p>
            Select automatic updates to the data or a static time frame.
            All data are subject to revision.
          </p>
          <label>
          <input
            type="radio"
            checked={selectedEmbedRange==="latest"}
            onChange={()=>setSelectedEmbedRange("latest")}
          />
            Always chart from 2022-07-01 to the last value available.
          </label>


          <label>
          <input
            type="radio"
            checked={selectedEmbedRange==="5years"}
            onChange={()=>setSelectedEmbedRange("5years")}
            />

            Always chart the last 5 years of data available.
          </label>

          <label>
          <input type="radio" checked={selectedEmbedRange==="fixed"}
          onChange={()=>setSelectedEmbedRange("fixed")}
          />
            Always chart from 2022-07-01 to 2026-07-01.
          </label>

          <textarea readOnly value={getEmbedCode()}/>
          <div className="embed-actions">

          <button className="btn-edit" onClick={copyEmbedCode}>
            {copied ? "Copied" : "Copy Code"}
          </button>

          <button className="btn-download" onClick={()=>setEmbedOpen(false)}>
            Close
          </button>

          </div>
          </div>
          </div>

        )}


        {customLinkOpen && (

          <div className="embed-overlay">

          <div className="embed-modal">

          <h3>
          Custom Graph Link
          </h3>


          <p>
          Select automatic updates to the data or a static time frame.
          All data are subject to revision.
          </p>


          <label>

          <input
          type="radio"
          checked={
          selectedEmbedRange==="latest"
          }
          onChange={()=>
          setSelectedEmbedRange("latest")
          }
          />

          Always chart from 2022-07-01 to the last value available.

          </label>



          <label>

          <input
          type="radio"
          checked={
          selectedEmbedRange==="5years"
          }
          onChange={()=>
          setSelectedEmbedRange("5years")
          }
          />

          Always chart the last 5 years of data available.

          </label>



          <label>

          <input
          type="radio"
          checked={
          selectedEmbedRange==="fixed"
          }
          onChange={()=>
          setSelectedEmbedRange("fixed")
          }
          />

          Always chart from 2022-07-01 to 2026-07-01.

          </label>



          <textarea
          readOnly
          value={getCustomGraphLink()}
          />



          <div className="embed-actions">

          <button className="btn-edit"
          onClick={()=>{
          navigator.clipboard.writeText(
          getCustomGraphLink()
          );
          setCopied(true);
          }}
          >
          {copied ? "Copied" : "Copy"}
          </button>


          <button className="btn-edit"
          onClick={()=>{
          setCustomLinkOpen(false)
          }}
          >
          Close
          </button>

          </div>


          </div>

          </div>

        )}
      </div>
    </main>
    
    

  );
}


export default DataVisualizer;